import React, { useState } from 'react';

/**
 * Generates the monkey patch template code as a string
 */
const generateMonkeyPatchTemplate = () => {
    return `// GPS Monkey Patch - Robust Version
// This overrides navigator.geolocation to accept simulated GPS data

// IMPORTANT: Make sure to BLOCK real GPS permission in browser for this site

// Initialize or reset global simulation state
window.MONKEY_PATCH_VERSION = 3;

if (!window.simulationState) {
    window.simulationState = {
        activeTimeout: null,
        currentSimId: 0,
        watchers: {}, // Changed from array to object map for proper removal
        watcherNextId: 1,
        fakeLocation: {
            coords: {
                latitude: 61.4978,  // Tampere starting coordinates
                longitude: 23.7610,
                accuracy: 10,
                altitude: null,
                altitudeAccuracy: null,
                heading: null,
                speed: null
            },
            timestamp: Date.now()
        }
    };
}

// Override getCurrentPosition
navigator.geolocation.getCurrentPosition = function(success, error, options) {
    success(window.simulationState.fakeLocation);
};

// Override watchPosition
navigator.geolocation.watchPosition = function(success, error, options) {
    const id = window.simulationState.watcherNextId++;
    window.simulationState.watchers[id] = success;
    // console.log('👀 New watcher added with ID:', id);
    success(window.simulationState.fakeLocation);
    return id;
};

// Override clearWatch
navigator.geolocation.clearWatch = function(id) {
    if (window.simulationState.watchers[id]) {
        // console.log('🛑 clearWatch removing ID:', id);
        delete window.simulationState.watchers[id];
    }
};

// Function to manually update position
// simId param allows ensuring updates come from the valid active simulation
window.updateGPS = function(lat, lng, heading, speed, simId) {
    // If a simulation is running (activeTimeout exists) and simId is provided,
    // ensure it matches the current simulation ID.
    // This prevents "zombie" loops from previous scripts from fighting the new one.
    if (window.simulationState.activeTimeout && simId !== undefined && simId !== window.simulationState.currentSimId) {
        console.warn('Ignored GPS update from zombie simulation (simId: ' + simId + ', current: ' + window.simulationState.currentSimId + ')');
        return;
    }

    const loc = window.simulationState.fakeLocation;
    loc.coords.latitude = lat;
    loc.coords.longitude = lng;
    if (heading !== undefined) loc.coords.heading = heading;
    if (speed !== undefined) loc.coords.speed = speed;
    loc.timestamp = Date.now();
    
    // Iterate over watcher IDs
    Object.values(window.simulationState.watchers).forEach(callback => callback(loc));
    console.log('📍 GPS updated: ' + lat.toFixed(5) + ', ' + lng.toFixed(5) + ' (SimId: ' + (simId || 'Manual') + ')');
};

// Function to stop active simulation
window.stopDriving = function() {
    if (window.simulationState.activeTimeout) {
        clearTimeout(window.simulationState.activeTimeout);
        window.simulationState.activeTimeout = null;
        console.log('⏹️  Simulation stopped');
        return true;
    }
    return false;
};

// Function to start driving simulation
window.startSimulation = function() {
    // Stop any existing simulation
    window.stopDriving();
    
    // Increment ID for the new session
    window.simulationState.currentSimId = (window.simulationState.currentSimId || 0) + 1;
    const mySimId = window.simulationState.currentSimId;
    
    if (!window.routeSteps || window.routeSteps.length === 0) {
        console.error('❌ No route steps loaded! Use window.routeSteps = [...] or copy route script.');
        return;
    }
    
    let index = 0;
    const steps = window.routeSteps;
    console.log('🚗 Starting GPS simulation #' + mySimId + ' with', steps.length, 'steps');
    
    // Set start immediately
    window.updateGPS(steps[0].lat, steps[0].lng, steps[0].heading, steps[0].speed, mySimId);
    
    const drive = () => {
        // Double check we are still the active simulation
        if (mySimId !== window.simulationState.currentSimId) return;

        if (index >= steps.length) {
            console.log('🏁 Simulation complete!');
            window.simulationState.activeTimeout = null;
            return;
        }
        
        const step = steps[index];
        window.updateGPS(step.lat, step.lng, step.heading, step.speed, mySimId);
        index++;
        
        if (index < steps.length) {
            const delay = step.delay || 1000;
            window.simulationState.activeTimeout = setTimeout(drive, delay);
        }
    };
    
    // Start loop
    drive();
};

console.log('✅ GPS Monkey Patch installed (Robust Mode v3)!');
console.log('   Global state: window.simulationState');
console.log('   To start: window.startSimulation()');
console.log('   To stop:  window.stopDriving()');
`;
};

/**
 * Component that provides copy-pastable monkey patch template
 */
const MonkeyPatchGenerator = () => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const template = generateMonkeyPatchTemplate();
        navigator.clipboard.writeText(template).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
            <div style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                GPS Simulation Setup
            </div>
            <div style={{ fontSize: '10px', marginBottom: '5px', color: '#666' }}>
                Step 1: Copy monkey patch to console
            </div>
            <button
                onClick={handleCopy}
                style={{
                    width: '100%',
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: copied ? '#4CAF50' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}
            >
                {copied ? '✓ Copied!' : '📋 Copy Monkey Patch'}
            </button>
            <div style={{ fontSize: '9px', marginTop: '3px', color: '#999' }}>
                Paste into browser console (F12) once per session
            </div>
        </div>
    );
};

export default MonkeyPatchGenerator;
